<template>
  <section :class="classes">
    <context-alert
      v-if="error"
      class="cordel-sections-error"
    />

    <ul class="cordel-sections">
      <li
        v-for="(section, i) in visible"
        :key="section._ID"
        class="cordel-section"
      >
        <typography-heading
          v-if="section.title"
          :hierarchy="section.hierarchy"
        >
          {{ section.title }}

          <template v-slot:subtitle>
            {{ section.subtitle }}
          </template>
        </typography-heading>

        <slot :name="section._ID" />

        <ul class="cordel-section-list">
          <template
            v-for="(field, j) in visible[i].fields"
          >
            <hr
              v-if="classified[i][j].includes('state--broken')"
              :key="`${field._ID}-breaker`"
              class="cordel-section-list-breaker"
            >

            <li
              :key="field._ID"
              :class="classified[i][j]"
            >
              <form-field
                ref="field"

                :type="field.type"

                :name="field._ID"

                :label="field.label"
                :labelless="field.labelless"
                :placeholder="field.placeholder"

                :disabled="field.disabled"
                :loading="field.loading"

                :errors="field.errors"
                :validations="field.validations"
                :callback="field.callback"

                :mask="field.mask"
                :options="field.options"

                :value="field.value"

                :icon="field.icon"

                @input="value => input(field._ID, value)"
                @search="value => search(field._ID, value)"

                @paste="e => paste(field._ID, e)"

                @validation="errors => validation(field._ID, errors)"
              />
            </li>
          </template>
        </ul>

        <context-separator class="cordel-sections-separator" />
      </li>
    </ul>

    <form-button-group
      class="cordel-sections-buttons"
      align="right"
      mobile
    >
      <form-button
        :loading="loading"
        @click="submit"
      >
        <slot name="submit">
          Salvar
        </slot>
      </form-button>
    </form-button-group>
  </section>
</template>

<script>
  import form from '@cordel/helpers/form/mixin';

  import ContextAlert from '@cordel/components/Context/Alert.vue';
  import ContextSeparator from '@cordel/components/Context/Separator.vue';

  import FormButton from '@cordel/components/Form/Button.vue';
  import FormButtonGroup from '@cordel/components/Form/Button/Group.vue';

  import FormField from '@cordel/components/Form/Field.vue';

  import TypographyHeading from '@cordel/components/Typography/Heading.vue';

  export default {
    name: 'CordelSections',
    components: {
      ContextAlert,
      ContextSeparator,

      FormButton,
      FormButtonGroup,
      FormField,

      TypographyHeading,
    },
    mixins: [form],
    props: {
      schema: {
        type: Object,
        default() {
          return {};
        },
      },
      loading: {
        type: Boolean,
        default: false,
      },
      error: {
        type: [String, Boolean, Object, Error],
        default: false,
      },
      buttonless: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      classes() {
        const classes = ['cordel-form'];

        if (this.buttonless) {
          classes.push('variant--buttonless');
        }

        return classes;
      },
      classified() {
        return this.visible.map(
          section => Object
            .values(section.fields)
            .map((field, index) => this.classify(section.fields, index)),
        );
      },
    },
    methods: {
      submit(e) {
        this.$refs.field.forEach(field => field.validate());

        if (this.invalid) {
          this.$emit('error', true);

          try {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          } catch (err) {
            window.scrollTo(0, 0);
          }
        } else {
          this.$emit('submit', e);
        }
      },
      paste(field, e) {
        this.$emit('paste', field, e);
      },
      input(field, value) {
        this.updateValue(field, value);
        this.$emit('change', field, value);
      },
      search(field, value) {
        this.$emit('search', field, value);
      },
      validation(field, errors) {
        this.updateErrors(field, errors);
      },
      classify(fields, index) {
        const field = fields[index];
        const previous = fields[index - 1];

        const classified = [
          'cordel-section-item',
          `size--${field.size}`,
        ];

        if (previous && previous.subsection !== field.subsection && !/UNBREAKABLE/.test(field.subsection)) {
          classified.push('state--broken');
        }

        return classified;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .cordel-sections {
    list-style: none;
    margin: 0 var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: 0 var(--product--pwa--horizontal);
    }
  }

  $columns: 12;

  .cordel-section {
    .variant--buttonless &:last-child {
      .cordel-sections-separator {
        display: none;
      }
    }
  }

  .cordel-section-list {
    column-gap: var(--cordel-spacing-s);
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    list-style: none;
  }

  .cordel-section-list-breaker {
    appearance: none;
    background-color: transparent;
    background-image: none;
    border: none;
    border-radius: 0;
    box-shadow: none;
    display: block;
    grid-column-start: span 12;
  }

  .cordel-section-item {
    margin-top: var(--cordel-spacing-giga);
  }

  @for $i from 1 through $columns {
    .size--#{$i} {
      grid-column-start: span $i;
    }
  }

  @media all and (max-width: $cordel-breakpoint-mobile) {
    .cordel-section-item {
      grid-column-start: span 12;
    }
  }

  .cordel-sections-buttons {
    margin: var(--cordel-spacing-blaster);
    margin-bottom: 0;
    padding-bottom: var(--cordel-spacing-blaster);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: var(--cordel-spacing-s);
      margin-bottom: 0;
      padding-bottom: var(--cordel-spacing-giga);
    }

    .variant--buttonless & {
      display: none;
    }
  }

  .cordel-sections-error {
    margin-bottom: var(--cordel-spacing-xl);
  }
</style>
